import { useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { useSolutionOrg } from '../../config/useSolutionOrg';
import { getAppDomain, SERVICE_NATION_BY_EVER_PRO_DOMAIN } from '../../config/domains';
import { Logger } from '../../config/logging/logger';
import { InvoiceSimpleSolutionOrgName, OPTIMUS_EVENT_FOR_EXTERNAL_LINK } from '../constants';
import { useOptimizelyFlag } from '../hooks/useOptimizelyFlag';
import { EngagementNavigationExternalLinksFlag } from '../OptimizelyFlagsConstants';
import { normalizeExternalUrl } from './urlUtils';
import { getServiceNationDestinationUrl, withTimeout } from './serviceNationLinks';
import { useServiceNationUrl } from './useServiceNationUrl';

// Past this, the member is sent to the original Service Nation link instead of waiting
export const SERVICE_NATION_TOKEN_TIMEOUT_MS = 10000;

const logger = Logger.create('useNavigationUtils');

declare global {
  interface Window {
    webkit?: {
      messageHandlers?: {
        [key: string]: {
          postMessage: (message: string) => void;
        };
      };
    };
    android?: {
      postMessage: (message: string) => void;
    };
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

// eslint-disable-next-line no-shadow
export enum NavigationTarget{
  Webview = 'webview',
  Browser = 'browser'
}

export const useNavigationUtils = () => {
  const { isNativeAppExperience, solutionOrgName } = useSolutionOrg();
  const isInvoiceSimple = useMemo(() => (
    solutionOrgName === InvoiceSimpleSolutionOrgName
  ), [solutionOrgName]);

  const {
    decision: externalLinkDecision,
    isDecisionReady: isExternalLinkDecisionReady,
  } = useOptimizelyFlag({
    flag: EngagementNavigationExternalLinksFlag,
  });

  const { buildServiceNationUrlFromLink } = useServiceNationUrl();
  const isServiceNationApp = getAppDomain() === SERVICE_NATION_BY_EVER_PRO_DOMAIN;

  // Opens the URL as is. Use openExternalLink, which also signs Service Nation links.
  const openUrl = useCallback((url: string, navigationTarget: NavigationTarget =
  NavigationTarget.Browser) => {
    if (!url) return;

    const normalizedUrl = normalizeExternalUrl(url);
    if (!normalizedUrl) return;

    // optimizely flag off behavior
    if (!isExternalLinkDecisionReady || !externalLinkDecision?.enabled) {
      if (navigationTarget === NavigationTarget.Webview) {
        window.open(normalizedUrl, '_parent');
      } else {
        window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    // Invoice Simple special-case (open in same view)
    // TODO: have invoice simple implement
    // navigationTarget feature
    if (navigationTarget === NavigationTarget.Webview && isInvoiceSimple
    ) {
      window.open(normalizedUrl, '_parent');
      return;
    }

    const messagePayload = { type: 'openExternal', url: normalizedUrl, target: navigationTarget };
    const messagePayloadStr = JSON.stringify(messagePayload);

    // Iframe experience
    if (isNativeAppExperience) {
      // send message to script to communicate with native app
      window.parent.postMessage({
        type: OPTIMUS_EVENT_FOR_EXTERNAL_LINK,
        payload: messagePayload,
      }, '*');
      return;
    }

    // Outside iframe but inside joist or invoice simple app
    if (window?.webkit?.messageHandlers?.optimusEvent?.postMessage) {
      // Joist iOS app WebView (WKWebView) compatibility
      window.webkit.messageHandlers.optimusEvent.postMessage(messagePayloadStr);
      return;
    }
    if (window?.android?.postMessage) {
      // Joist android app webView
      window.android.postMessage(messagePayloadStr);
      return;
    }
    if (window?.ReactNativeWebView?.postMessage) {
      // React Native IOS or Android WebView (invoice simple)
      window.ReactNativeWebView.postMessage(messagePayloadStr);
      return;
    }

    // Normal browser/desktop experience
    if (navigationTarget === NavigationTarget.Webview) {
      window.open(normalizedUrl, '_parent');
    } else {
      window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
    }
  }, [isNativeAppExperience,
    isExternalLinkDecisionReady,
    externalLinkDecision?.enabled,
    isInvoiceSimple,
  ]);

  // For URLs that need an async request (e.g. an SSO token), call this directly
  // from the click handler so desktop browsers can open the tab before awaiting.
  const openExternalLinkAsync = useCallback(async (buildUrl: () => Promise<string>) => {
    const usesNativeBridge = isExternalLinkDecisionReady && externalLinkDecision?.enabled && (
      isNativeAppExperience ||
      window.webkit?.messageHandlers?.optimusEvent?.postMessage ||
      window.android?.postMessage ||
      window.ReactNativeWebView?.postMessage
    );
    if (usesNativeBridge) {
      openUrl(await buildUrl());
      return;
    }

    // Passing noopener/noreferrer to window.open returns null even on success.
    // Keep a handle for navigation, then sever the opener before loading anything.
    const tab = window.open('about:blank', '_blank');
    try {
      if (tab) tab.opener = null;
      const url = normalizeExternalUrl(await buildUrl());
      if (!url) throw new Error('No external link URL was returned');
      // Popup blockers and window.open wrappers may not return a tab handle.
      // Complete SSO in the current tab rather than require a page reload.
      if (!tab) {
        window.location.assign(url);
        return;
      }
      if (tab.closed) throw new Error('The browser tab was closed before navigation');
      // Navigate within the reserved tab and keep the existing noreferrer policy.
      const link = tab.document.createElement('a');
      link.href = url;
      link.rel = 'noreferrer';
      link.target = '_self';
      tab.document.body.appendChild(link);
      link.click();
    } catch (error) {
      tab?.close();
      throw error;
    }
  }, [isExternalLinkDecisionReady, externalLinkDecision?.enabled,
    isNativeAppExperience, openUrl]);

  /**
   * Opens an external URL. In the Service Nation app, links to Service Nation pages are opened
   * through SN's `/edge-auth` with a fresh SSO token (passed as `destinationUrl`, SN-1167), so the
   * member lands on that exact page signed in. This covers every caller: content cards, partner
   * resources and the document-wide link interceptor alike.
   *
   * Call it from the click handler itself: for new tabs the tab is opened before the token
   * request, so the popup blocker does not stop it. When the token request fails or takes over
   * SERVICE_NATION_TOKEN_TIMEOUT_MS, the original link is opened.
   */
  const openExternalLink = useCallback((url: string, navigationTarget: NavigationTarget =
  NavigationTarget.Browser) => {
    // Normalized first, so scheme-less links (e.g. `members.servicenation.com/...`) are signed too
    const normalizedUrl = normalizeExternalUrl(url);
    const destinationUrl = isServiceNationApp && normalizedUrl ?
      getServiceNationDestinationUrl(normalizedUrl) :
      null;
    if (!destinationUrl) {
      openUrl(url, navigationTarget);
      return;
    }

    // Never rejects: on failure the member gets the original link
    const buildUrl = () => withTimeout(
      buildServiceNationUrlFromLink(destinationUrl),
      SERVICE_NATION_TOKEN_TIMEOUT_MS,
    ).catch((error) => {
      logger.error(Logger.format('Failed to generate Service Nation SSO token', error));
      Sentry.captureException(error, {
        tags: { feature: 'serviceNationLinkSso' },
        extra: { destinationUrl },
      });
      return url;
    });

    if (navigationTarget === NavigationTarget.Webview) {
      // Same-tab navigation needs no popup, so it can wait for the token
      buildUrl().then((signedUrl) => openUrl(signedUrl, NavigationTarget.Webview));
      return;
    }

    openExternalLinkAsync(buildUrl).catch((error) => {
      logger.error(Logger.format('Failed to open Service Nation link in a new tab', error));
      openUrl(url);
    });
  }, [isServiceNationApp, buildServiceNationUrlFromLink, openUrl, openExternalLinkAsync]);

  return { openExternalLink, openExternalLinkAsync };
};
